/**
 * variable-context.ts — Pure variable resolver for F6 {{interpolation}}.
 *
 * PURE MODULE: no React, no DOM, no window imports.
 * Safe to run in Node.js test environments.
 *
 * Exported:
 *   VariableContext — snapshot of all variable sources at render time.
 *   resolveVariable(name, ctx) — dispatch by prefix, format by block type.
 *
 * Sources:
 *   answer.<blockId>  — user answer (id→label resolved for choice blocks).
 *   query.<param>     — URL query parameter.
 *   geo.country       — ISO 3166-1 alpha-2 from geo-IP.
 *   device.os         — OS identifier (ios/android/windows/macos/linux).
 *   browser           — browser identifier (safari/chrome/firefox/...).
 *   email             — captured user email.
 *   user.id           — visitor/user GUID.
 *   <variableName>    — named alias for answer.<blockId> (WEB-115).
 *   bmi, bmiLevel, normalWeightMin, … — вычисляемые значения из ответов роста
 *                       и веса (решение владельца №5, `computedValues.ts`).
 *
 * D1 RATIFIED: answers[blockId] = option.id (NOT option.label).
 * D2: id→label resolved per-language from blockOptions (current content after swap).
 * D7: URL fields must NOT be passed through interpolation — caller responsibility.
 *
 * Fail-open: unknown or unset variable → empty string (never throws, no literal).
 * Non-recursive: single replacement pass (value containing {{y}} is not re-scanned).
 */

import { isComputedVariableName, type MeasureSources } from './measureSources';
// WEB-3447: сам счёт вычисляемых значений — ленивым куском через дверь; на
// руках нет (воронке не нужен или ещё едет) — пусто, как любая незаполненная.
import { playerKitDoor } from './playerKitDoor';

export interface BlockOption {
  id: string;
  label: string;
}

export interface VariableContextSystem {
  /** ISO 3166-1 alpha-2 country code from geo-IP, or null/undefined if unknown. */
  geoCountry?: string | null;
  /** OS identifier, e.g. "ios", "android", "windows". */
  deviceOs?: string;
  /** Browser identifier, e.g. "safari", "chrome". */
  browser?: string;
  /** Captured user email. */
  email?: string;
  /** Visitor/user GUID from identity-link. May be undefined until first-touch. */
  userId?: string;
  /**
   * BCP-47 locale of the funnel (e.g. "en", "de-DE"), used to localize
   * date-typed answers in {{interpolation}} (WEB-182 §F6). Undefined → the
   * runtime default locale is used.
   */
  locale?: string;
}

/**
 * BlockMeta — minimal per-block descriptor used to format an answer by its
 * block type during {{interpolation}} (WEB-182 §F6):
 *   - date_picker        → answer (ISO yyyy-mm-dd) rendered in the funnel locale
 *   - toggle / checkbox  → block `label` when affirmed ('true'), else empty
 *                          (never the literal "true"/"false")
 *   - number/slider/rating/text → raw value passthrough (already a string)
 */
export interface BlockMeta {
  type: string;
  /** Label/content of the block, shown for an affirmed toggle/checkbox. */
  label?: string;
  /**
   * WEB-1301: default measurement unit for weight_picker ('kg'/'lb') /
   * height_picker ('cm'/'ft'), used to append a unit suffix to the raw
   * numeric answer in the loader "your answers" summary when the visitor
   * hasn't toggled a different unit (the toggled case is read from the
   * companion answers[pickerUnitValueKey(blockId)] entry instead — see
   * pickerUnitValueKey in quiz-runtime/answerCollection.ts). Undefined for
   * every other block type.
   */
  unitDefault?: string;
}

/**
 * VariableContext — flat snapshot of all variable sources for F6 interpolation.
 *
 * Built by PublicQuiz/PublicPaywall at render time:
 *   - answers:      from FormValuesContext (user choices / text input)
 *   - urlParams:    from window.location.search
 *   - system:       from quiz response (geo_country) + browser/UA heuristics
 *   - blockOptions: from current quiz content for id→label lookup per-language
 *
 * answers[blockId]:
 *   - single_choice / select_dropdown → string (option.id)
 *   - multiple_choice                 → string[] (option.id[])
 *   - text / email / number / slider  → string (raw value)
 *
 * blockOptions[blockId]:
 *   Array of {id, label} from current (possibly-swapped) content.
 *   Empty/absent for non-choice blocks (text, email, etc.).
 *   id is language-stable; label is per-language.
 */
export interface VariableContext {
  answers: Record<string, string | string[]>;
  urlParams: Record<string, string>;
  system: VariableContextSystem;
  /** Record<blockId, {id, label}[]> for id→label lookup. */
  blockOptions: Record<string, BlockOption[]>;
  /**
   * WEB-115: Named variable registry — maps variableName → blockId.
   * Built from quiz content blocks that have a variableName set.
   * Allows {{myVar}} to resolve to answer.blockId in the player.
   * Optional for backward compatibility — old VariableContext without this field is valid.
   */
  namedVariables?: Record<string, string>;
  /**
   * WEB-182: per-block type/label descriptors for format-by-type. Optional for
   * backward compatibility — absent entry → raw value passthrough (legacy F6
   * behavior). Built from current funnel content alongside blockOptions.
   */
  blockMeta?: Record<string, BlockMeta>;
  /**
   * Решение владельца №5 (повтор Slimkit): какие пикеры дают рост, текущий и
   * желаемый вес для вычисляемых переменных ({{bmi}} и др.). Собирается
   * `collectMeasureSources(content)`. Необязательное: без него рост и текущий
   * вес берутся из `blockMeta` (первые пикеры), желаемого веса нет.
   */
  measureSources?: MeasureSources;
  /**
   * WEB-2677 (возврат 1, решение оркестратора 03.11): ответы квиза во
   * встроенном пейволле — только на чтение и ОТДЕЛЬНО от `answers`. Их читает
   * подстановка `{{answer.<id>}}` (своё значение главнее), а сводка ответов
   * загрузчика (`LoaderRenderer`) смотрит только в `answers` — сырые id
   * вариантов и почта квиза покупателю там не показываются.
   */
  quizAnswers?: Record<string, string | string[]>;
}

/**
 * formatDateForLocale — render an ISO `yyyy-mm-dd` date string in the given
 * locale (funnel locale). Constructs the date from explicit parts to avoid
 * timezone shifting that `new Date(isoString)` (UTC midnight) would introduce.
 * Fail-open: a value that is not a plain ISO date is returned unchanged.
 */
function formatDateForLocale(value: string, locale?: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return value;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return date.toLocaleDateString(locale || undefined);
  } catch {
    return value;
  }
}

/**
 * formatByType — format a single (non-array, non-choice) answer string by its
 * block type for {{interpolation}} (WEB-182 §F6). Returns the display string.
 *   date_picker        → locale-formatted date
 *   toggle / checkbox  → block label when 'true', empty otherwise
 *   everything else    → raw passthrough (number/slider/rating already strings)
 */
function formatByType(
  raw: string,
  meta: BlockMeta | undefined,
  system: VariableContextSystem,
): string {
  if (!meta) return raw;
  switch (meta.type) {
    case 'date_picker':
      return formatDateForLocale(raw, system.locale);
    case 'toggle':
    case 'checkbox':
      // Affirmed → block label; never leak the literal "true"/"false".
      return raw === 'true' ? (meta.label ?? '') : '';
    default:
      return raw;
  }
}

/**
 * collectNamedVariables — build the variableName → blockId registry from funnel
 * content. Walks every screen/block; a block with a non-empty `variableName`
 * registers `variableName → block.id` so {{variableName}} resolves to that
 * block's answer in the player (WEB-115). Last occurrence wins on duplicate
 * names. Pure, fail-safe (non-array / malformed → {}).
 */
export function collectNamedVariables(
  content: unknown[],
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!Array.isArray(content)) return out;
  for (const screen of content) {
    if (typeof screen !== 'object' || screen === null) continue;
    const blocks = (screen as { blocks?: unknown }).blocks;
    if (!Array.isArray(blocks)) continue;
    for (const block of blocks) {
      if (typeof block !== 'object' || block === null) continue;
      const b = block as { id?: unknown; variableName?: unknown };
      if (
        typeof b.variableName === 'string' &&
        b.variableName.trim().length > 0 &&
        typeof b.id === 'string' &&
        b.id.length > 0
      ) {
        out[b.variableName] = b.id;
      }
    }
  }
  return out;
}

/**
 * resolveVariable — resolve one variable name against a VariableContext.
 *
 * Dispatch order:
 *   1. "answer.<blockId>" prefix → answers + id→label via blockOptions
 *   2. "query.<param>"   prefix → urlParams
 *   3. Named variable lookup → namedVariables[name] → answer.<blockId> (WEB-115;
 *      с WEB-2090 п.5.5 — РАНЬШЕ системных имён)
 *   4. System key switch → system.*
 *   5. Computed body values (bmi, goalDate, …) → computedValues.ts
 *   6. Unknown → empty string (fail-open)
 *
 * Never throws. Never mutates ctx.
 */
export function resolveVariable(name: string, ctx: VariableContext): string {
  // --- 1. answer.* ---
  if (name.startsWith('answer.')) {
    const blockId = name.slice('answer.'.length);
    const raw = ctx.answers[blockId] ?? ctx.quizAnswers?.[blockId];

    // Unset or empty → fail-open
    if (raw == null || raw === '') return '';
    if (Array.isArray(raw) && raw.length === 0) return '';

    const opts = ctx.blockOptions[blockId] ?? [];

    if (Array.isArray(raw)) {
      // multi_select: map ids to labels, join with ", " (display-only)
      return raw
        .map((id) => opts.find((o) => o.id === id)?.label ?? id)
        .join(', ');
    }

    // Single value: if choice block, resolve id→label; else format by type.
    if (opts.length > 0) {
      return opts.find((o) => o.id === raw)?.label ?? raw;
    }
    return formatByType(raw, ctx.blockMeta?.[blockId], ctx.system);
  }

  // --- 2. query.* ---
  if (name.startsWith('query.')) {
    const param = name.slice('query.'.length);
    return ctx.urlParams[param] ?? '';
  }

  // --- 3. Named variable lookup (WEB-115) — РАНЬШЕ системных имён ---
  // WEB-1368 п.5.5 (WEB-2090 ч.2в): переменная автора с системным именем
  // (`email`, `browser`, `user.id`…) раньше никогда не подставлялась —
  // разбор сначала шёл по системным именам и отдавал системное значение или
  // пустую строку, без единой ошибки. Явно заданное автором имя важнее
  // встроенного: автор видит свою переменную в реестре и ждёт её значение.
  // variableName → blockId → answer.blockId resolution
  if (ctx.namedVariables) {
    const blockId = ctx.namedVariables[name];
    if (blockId !== undefined) {
      return resolveVariable(`answer.${blockId}`, ctx);
    }
  }

  // --- 4. System vars (после авторских имён) ---
  switch (name) {
    case 'geo.country':
      return ctx.system.geoCountry ?? '';
    case 'device.os':
      return ctx.system.deviceOs ?? '';
    case 'browser':
      return ctx.system.browser ?? '';
    case 'email':
      return ctx.system.email ?? '';
    case 'user.id':
      return ctx.system.userId ?? '';
    default:
      break;
  }

  // --- 5. Вычисляемые значения (решение владельца №5) — ПОСЛЕ имён автора
  // и системных: авторская переменная `bmi` важнее вычисления. ---
  if (isComputedVariableName(name)) {
    return (
      playerKitDoor.get()?.computedValues.resolveComputedVariable(name, ctx) ??
      ''
    );
  }

  // --- 6. Unknown → fail-open ---
  return '';
}
